import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { filter, tap, withLatestFrom } from 'rxjs/operators';
import { PathTestMapPoint } from '../core/models/mapping';
import { PathTestPoint, PathTestPointSeq } from '../core/models/path-test';
import { activity_status } from '../core/models/task-status';
import { DriveTestDoc } from '../core/schema/drive-test-schema';
import { PathTestDoc } from '../core/schema/path-test-schema';
import { PointDoc } from '../core/schema/point-schema';
import { DriveTestService, PathTestService, PathWaypointDoc, PathWaypointService } from '../core/services';

export class PathViewStateMapData {
    pathWaypointDocs: PathWaypointDoc[]
    pointDocMap: Map<string, PointDoc>
    mapPointsByTestPtId: { [key: string]: PathTestMapPoint }
    mapPoints: PathTestMapPoint[]
}

export interface PathViewState {
    pathTestDoc: PathTestDoc | DriveTestDoc
    testPoints: PathTestPoint[]
    testPoint: PathTestPoint
    testPointIndex: number
    seqs: PathTestPointSeq[]
    seq: PathTestPointSeq
    seqIndex: number
    mapData: PathViewStateMapData
    mapPoint: PathTestMapPoint
}


@Injectable()
export class PathViewStore extends ComponentStore<PathViewState> {
    constructor(
        public pathWaypointService: PathWaypointService,
        public pathTestService: PathTestService,
        public driveTestService: DriveTestService
    ) {
        super({
            pathTestDoc: null,
            testPoints: [],
            testPoint: null,
            testPointIndex: null,
            seqs: [],
            seq: null,
            seqIndex: null,
            mapData: null,
            mapPoint: null
        });
    }


    private initTestPointDependentData(state: PathViewState, testPoint: PathTestPoint) {
        const mapPoint = testPoint && state.mapData?.mapPoints.find(mp => mp.pathTestPoint?.id == testPoint.id)
        const seqs = testPoint?.seqs || []
        const seq = seqs?.last() || new PathTestPointSeq()
        const seqIndex = seqs.lastIndex() // -1 indicates not in array (temp seq not yet saved)
        return {mapPoint, seqs, seq, seqIndex}
    }

    // Start out on first testPoint and last seq when new pathTest selected
    readonly setPathTestDoc = this.updater((state, rxdbDoc: PathTestDoc | DriveTestDoc) => {
        console.log(`setPathTestDoc(${rxdbDoc.pathId})`)
        if (state.pathTestDoc?._id == rxdbDoc?._id) {
            console.warn('SAME pathTestDoc ' + rxdbDoc?._id)
        }
        const testPoints = rxdbDoc?.testPoints
        const testPoint = testPoints ? testPoints[0] : undefined
        const testPointIndex = testPoint ? 0 : -1

        let newState = {
            ...state,
            pathTestDoc: rxdbDoc,
            testPoints,
            testPoint,
            testPointIndex,
            mapData: null,
            ...this.initTestPointDependentData(state, testPoint)
        }
        //console.log('returning state with testpoint' + JSON.stringify(newState.testPoint))
        return newState
    });

    readonly changeTestPoint = this.updater((state, testPoint: PathTestPoint) => {
        console.log(`changeTestPoint(${testPoint.pointId})`)
        return {
            ...state,
            testPoint,
            testPointIndex: state.testPoints?.findIndex(tp => tp == testPoint),
            ...this.initTestPointDependentData(state, testPoint)
        }
    });

    readonly changeTestPointIndex = this.updater((state, testPointIndex: number) => {
        console.log(`changeTestPointIndex(${testPointIndex})`)
        const testPoint = state.testPoints && state.testPoints[testPointIndex]
        const tpData = this.initTestPointDependentData(state, testPoint)
        return {
            ...state,
            testPointIndex,
            testPoint,
            ...tpData
        }
    });

    readonly changeTestPointById = this.updater((state, testPointId: string) => {
        //console.log(`changeTestPointById(${testPointId})`)
        const testPointIndex = state.testPoints?.findIndex(tp => tp.id == testPointId)
        const testPoint = state.testPoints[testPointIndex]
        const tpData = this.initTestPointDependentData(state, testPoint)
        return {
            ...state,
            testPointIndex,
            testPoint,
            ...tpData
        }
    });

    //this is Point.id, not the PathTestPoint.id
    readonly changeTestPointByPointId = this.updater((state, pointId: string) => {
        //console.log(`changeTestPointById(pointId:${pointId})`)
        const testPointIndex = state.testPoints?.findIndex(tp => tp.pointId == pointId)
        const testPoint = state.testPoints[testPointIndex]
        const tpData = this.initTestPointDependentData(state, testPoint)
        return {
            ...state,
            testPointIndex,
            testPoint,
            ...tpData
        }
    });

    readonly changeTestPointByOrder = this.updater((state, order: number) => {
        console.log(`changeTestPointByOrder(order:${order})`)
        const testPointIndex = state.testPoints?.findIndex(tp => tp.order == order)
        const testPoint = state.testPoints[testPointIndex]
        const tpData = this.initTestPointDependentData(state, testPoint)
        const newState = {
            ...state,
            testPointIndex,
            testPoint,
            ...tpData
        }
        //console.log(`changeTestPointByOrder() returning state`)
        //console.dir(newState)
        return newState
    });

    readonly changeTestPoints = this.updater((state, testPoints: PathTestPoint[]) => {
        //console.log(`changeTestPoints(${testPoints?.length})`)
        //console.log(`state.testPoint (routeId=${state.testPoint.routeId}):`)
        //console.dir(state)
        const testPointIndex = state.testPoint ? 
            (state.testPoint.routeId ? 
                testPoints?.findIndex(tp => tp.routeId == state.testPoint.routeId && tp.order === state.testPoint.order) :
                testPoints?.findIndex(tp => tp.pointId == state.testPoint.pointId)) : -1
        const testPoint = testPoints[testPointIndex]
        const seqs = testPoint?.seqs || []
        var seq: PathTestPointSeq = null
        var seqIndex = -1

        if (state.seq && seqs?.length) {
            seqIndex = seqs.findIndex(s => s.id == state.seq.id)
            seq = seqs[seqIndex]
        }

        // check for an existing open/new seq not yet saved (this adds new seq back when tab regains focus)
        if (testPoint && !seq && state.seq && !state.seq.startTime) {
            const oldSeqIndex = state.seqs?.findIndex(s => s.id == state.seq.id)
            if (oldSeqIndex != -1) {
                seqs.push(state.seq)
                seq = state.seq
                seqIndex = seqs.lastIndex()
            } else if (state.seqs?.length == 0) {
                if (seqs.length) {
                    // the test point has a real seq with a start time so don't add the state.seq with no start time
                    seq = seqs.last()
                } else {
                    seqs.push(state.seq)
                    seq = state.seq
                }
                seqIndex = seqs.lastIndex()
            }
        }

        const newState = {
            ...state,
            testPoints,
            testPointIndex,
            testPoint,
            seqs,
            seq,
            seqIndex,

            // mapPoint and mapData should get rebuilt by???
            mapData: null,
            mapPoint: null
        }
        //console.log(`changeTestPoints() new state`)
        //console.dir(newState)
        return newState
    });

    readonly changeSeqs = this.updater((state, seqs: PathTestPointSeq[]) => {
        return {
            ...state,
            seqs,
        }
    });

    readonly changeSeqIndex = this.updater((state, seqIndex: number) => {
        const seq = state.seqs && state.seqs[seqIndex]
        console.log(`changeSeqIndex(${seqIndex})`)
        return {
            ...state,
            seqIndex,
            seq,
        }
    });

    readonly pathTestDoc$ = this.select(state => state.pathTestDoc);
    readonly testPoints$ = this.select(state => state.testPoints);
    readonly seq$ = this.select(state => state.seq)

    readonly hasPreviousTestPoint$ = this.select(
        this.state$,
        ({testPointIndex, testPoints}) => testPointIndex > 0 && testPoints?.length > 0
    );

    readonly hasNextTestPoint$ = this.select(
        this.state$,
        ({testPointIndex, testPoints}) => testPointIndex < testPoints?.length - 1
    );

    readonly hasPreviousSeq$ = this.select(
        this.state$,
        state => state.seqIndex > 0 && state.seqs?.length > 0
    );

    readonly hasNextSeq$ = this.select(
        this.state$,
        state => state.seqIndex < state.seqs?.length - 1
    );

    readonly vm$ = this.select(
        this.state$,
        this.hasPreviousTestPoint$,
        this.hasNextTestPoint$,
        this.hasPreviousSeq$,
        this.hasNextSeq$,
        (state, hasPreviousTestPoint, hasNextTestPoint, hasPreviousSeq, hasNextSeq) => {
            //console.log('returning vm: ' + JSON.stringify(state.testPoint) + ', index: ' + state.testPointIndex)
            return {
                ...state,
                hasPreviousTestPoint,
                hasNextTestPoint,
                hasPreviousSeq,
                hasNextSeq
            }
        }
    );

    readonly testPoint$ = this.select(this.state$, ({testPoint}) => testPoint)
    readonly testPointIndex$ = this.select(this.state$, ({testPointIndex}) => testPointIndex)
    readonly mapData$ = this.select(state => state.mapData, {debounce: true})
    readonly mapPoint$ = this.select(state => state.mapPoint)

    readonly buildMapData = this.effect(
        (waypointData$: Observable<{ pathWaypointDocs: PathWaypointDoc[], pointDocMap: Map<string, PointDoc> }>) =>
            waypointData$.pipe(
                withLatestFrom(this.testPoints$),
                withLatestFrom(this.testPoint$),
                tap(([[waypointData, testPoints], testPoint]) => {
                    const {pathWaypointDocs, pointDocMap} = waypointData
                    const mapData = this.initMapData(pathWaypointDocs, pointDocMap, testPoints)
                    const mapPoint = testPoint && mapData.mapPointsByTestPtId[testPoint.id]
                    this.patchState({
                        mapData,
                        mapPoint
                    })
                })
            )
    )

    readonly moveToNextTestPoint = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.hasNextTestPoint$),
            filter(([, hasNextTestPoint]) => hasNextTestPoint),
            tap(() => {
                const state = this.get()
                this.changeTestPointIndex(state.testPointIndex + 1);
            })
        );
    });

    readonly moveToLastTestPoint = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.hasNextTestPoint$),
            filter(([, hasNextTestPoint]) => hasNextTestPoint),
            tap(() => {
                const state = this.get()
                this.changeTestPointIndex(state.testPoints?.length - 1);
            })
        );
    });

    readonly moveToPreviousTestPoint = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.hasPreviousTestPoint$),
            filter(([, hasPreviousTestPoint]) => hasPreviousTestPoint),
            tap(() => {
                const state = this.get()
                this.changeTestPointIndex(state.testPointIndex - 1);
            })
        );
    });

    readonly moveToFirstTestPoint = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.hasPreviousTestPoint$),
            filter(([, hasPreviousTestPoint]) => hasPreviousTestPoint),
            tap(() => {
                this.changeTestPointIndex(0);
            })
        );
    });

    readonly moveToNextSeq = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.hasNextSeq$),
            filter(([, hasNextSeq]) => hasNextSeq),
            tap(() => {
                const state = this.get()
                this.changeSeqIndex(state.seqIndex + 1);
            })
        );
    });

    readonly moveToPreviousSeq = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.hasPreviousSeq$),
            filter(([, hasPreviousSeq]) => hasPreviousSeq),
            tap(() => {
                const state = this.get()
                this.changeSeqIndex(state.seqIndex - 1);
            })
        );
    });


    readonly newSeq = this.effect((trigger$) => {
        return trigger$.pipe(
            tap(() => {
                const state = this.get()
                var seqs = state.seqs || []
                const newSeqs = [...seqs, new PathTestPointSeq()]
                this.changeSeqs(newSeqs)
                this.changeSeqIndex(newSeqs.length - 1);
            })
        );
    });

    initMapData = (
        pathWaypointDocs: PathWaypointDoc[],
        pointDocMap: Map<string, PointDoc>,
        testPoints: PathTestPoint[]
    ): PathViewStateMapData => {
        console.log(`initMapData(pathWaypointDocs:${pathWaypointDocs?.length},testPoints:${testPoints?.length})`)
        const mapPoints = pathWaypointDocs?.map(pathWaypoint => {
            const point = pointDocMap.get(pathWaypoint.pointId)
            if (!point) {
                const msg = `PathWaypoint ${pathWaypoint.pathId} ${pathWaypoint.type} ${pathWaypoint.waypointOrder} pointId '${pathWaypoint.pointId}' not found in point table`
                console.warn(msg)
                this.pathTestService.publishError(msg, this)
            }
            const testPoint = testPoints?.find(tp => tp.pointId == pathWaypoint.pointId)

            return new PathTestMapPoint(pathWaypoint, point, testPoint)
        })
        this.pathTestService.updateTestPointArrayContext(mapPoints)
        //this.mapPoints.forEach(mp=>{
        //  console.log(`map test pt: ${mp.id}, tp.id=${mp.pathTestPoint?.id}, wp.pointId=${mp.pathWaypoint.pointId}, pt._id=${mp.point?._id}, parent=${mp.parentId}, wpOrder=${mp.pathWaypoint.waypointOrder}, lat=${mp.latitude} lng=${mp.longitude}`)      
        //})
        const mapPointsByTestPtId = mapPoints.filter(mp => mp.pathTestPoint).reduce((obj, mp) => {
            if (!mp.pathTestPoint?.id) {
                console.warn('Test path child point is missing unique id.  Point ID: ' + mp.pathTestPoint?.pointId)
            } else {
                obj[mp.pathTestPoint.id] = mp
            }
            return obj
        }, {})
        return {
            mapPoints,
            pathWaypointDocs,
            pointDocMap,
            mapPointsByTestPtId
        }
    }


    saveTestPointSeq = async (testPointId: string, srcSeq: PathTestPointSeq, statusId: activity_status.Id ):
        Promise<[PathTestPoint, PathTestPointSeq]> => {

        console.log(`saveTestPointSeq(${testPointId},${JSON.stringify(srcSeq)})`)
        const {pathTestDoc} = this.get()
        const updatedTaskDoc = await pathTestDoc.atomicUpdate(pathTestData => {
            let testPoint = pathTestData.testPoints?.find(pt => pt.id == testPointId)
            if (testPoint) {
                if ( statusId !== null ) {
                    testPoint.statusId = statusId
                }
                console.log(testPoint.pointId + ' seq cnt: ' + testPoint.seqs?.length)
                //console.log('testPoint:')
                //console.dir(testPoint)
                console.log('srcSeq:')
                console.dir(srcSeq)
                if (!testPoint.seqs) {
                    testPoint.seqs = []
                }
                let seqIndex = testPoint.seqs?.findIndex(s => s.id == srcSeq.id)
                //console.log(`seqIndex found where id==${srcSeq.id} => ${seqIndex}`)
                if (seqIndex == -1) {
                    seqIndex = testPoint.seqs.length
                }
                testPoint.seqs[seqIndex] = srcSeq
            } else {
                throw new Error('Test point not found in Test Path.')
            }
            return pathTestData
        })

        const updatedTestPoint = updatedTaskDoc.testPoints.find(tp => tp.id == testPointId)
        const updatedSeqs = updatedTestPoint?.seqs || []
        const updatedSeqIndex = updatedSeqs.findIndex(s => s.id == srcSeq.id)
        const updatedSeq = updatedSeqs[updatedSeqIndex]
        const state = {
            testPoint: updatedTestPoint,
            seqs: updatedSeqs,
            seq: updatedSeq,
            seqIndex: updatedSeqIndex
        }
        this.patchState(state)
        //console.log('updated pathStore after save:')
        //console.dir(state)
        return [updatedTestPoint, updatedSeq]
    }
}